import React, { useState, useEffect, useRef } from 'react';
import AlarmsDoughnut from 'components/shared/AlarmsDoughnut/AlarmsDoughnut';
import { ChartLegend } from '../ParametersView/ParametersGraph/ParametersGraph';
import { SingleCalendar } from 'components/shared/CalendarFilter/CalendarFilter';
import Dropdown from 'components/shared/Dropdown/Dropdown';
import classNames from 'classnames';
import { LOADING_CIRCLE } from 'common/utils/constants';
import { getAlarmAggregate } from 'apis/fmApis/GetAlarmAggregate';
import { DefaultPieChartColors } from 'common/utils/constants';
import { GET_LAST_COMMISSION_DATE_STATUS } from 'pages/PivotOverviewPage/utils';
import styles from './AlarmHistoryPanel.module.css';
import moment from 'moment';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { IconLoader } from 'common/IconLoader';
import WarningBox from 'components/shared/WarningBox/WarningBox';

const ERROR_TERMINAL_MESSAGE_1 = 'Sorry, we are unable to complete your request. Please try it again later.';
const ERROR_TERMINAL_MESSAGE_2 = 'If the problem persists, please ';
const LAST_COMMISSION_DATE_NOT_FOUND_WARNING_MESSAGE =
  'As the latest commissioned date is unavailable, please select a date.';

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const AlarmHistoryPanel = (props) => {
  const sinceDateMessage = props.isGa2 ? 'Since last year' : 'Since last commissioned';

  const [alarmAggregateData, setAlarmAggregateData] = useState([undefined, undefined, undefined]);
  const [viewBy, setViewBy] = useState('Alarms type');
  const [date, setDate] = useState(sinceDateMessage);
  const [rawDate, setRawDate] = useState();
  const [legendColors, setLegendColors] = useState({});
  const [isCalendarDateValid, setIsCalendarDateValid] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);

  const [requestSuccessCount, setRequestSuccessCount] = useState(0);
  const [requestError, setRequestError] = useState();

  const prevViewBy = usePrevious(viewBy);
  const prevDate = usePrevious(date);
  const prevDeviceData = usePrevious(props.deviceData);

  useEffect(() => {
    if (prevDeviceData == null) return;
    if (
      prevViewBy === viewBy &&
      prevDate === date &&
      JSON.stringify(props.deviceData) === JSON.stringify(prevDeviceData)
    ) {
      return;
    }
    if (!props.deviceData || props.deviceData.length === 0) return;
    setRequestSuccessCount(0);
    setLegendColors({});
    const getAggregate = async (deviceId, fieldName, index, fromDate, toDate) => {
      const response = await getAlarmAggregate(deviceId, fieldName, fromDate, toDate);
      setAlarmAggregateData((alarmAggregateData) => [
        ...alarmAggregateData.slice(0, index),
        response,
        ...alarmAggregateData.slice(index + 1)
      ]);
      setRequestSuccessCount((count) => count + 1);
    };

    const requestType = viewBy === 'Alarms type' ? 'alarmName' : 'severity';
    let fromDate;
    let toDate = moment().toISOString();

    if (!date.includes('Since')) {
      fromDate = rawDate.toISOString();
    } else {
      fromDate = props.deviceData[0].lastCommissionDate;
      // avoid query when still searching for last commissioned date
      if (
        fromDate === GET_LAST_COMMISSION_DATE_STATUS.PENDING ||
        fromDate === GET_LAST_COMMISSION_DATE_STATUS.NOT_FOUND
      )
        return;
    }

    props.deviceData.forEach((device, index) => {
      getAggregate(device.deviceId, requestType, index, fromDate, toDate).catch((e) => {
        console.log('error', e);
        setRequestError(e);
      });
    });
  }, [viewBy, date, props.deviceData]);

  const dateDropdownOnClick = (selection) => {
    if (selection === sinceDateMessage) {
      setDate(selection);
    } else {
      setShowCalendar(true);
    }
  };

  const handleDateSubmit = () => {
    setDate(moment(rawDate.toISOString()).format('MMM D, YYYY'));
    setShowCalendar(false);
  };

  /**
   * @returns modified date title based on default commission date or selected date
   */
  const getDateDropdownTitle = () => {
    if (date.includes('Since')) {
      return date;
    }

    return 'Since ' + date;
  };

  const isLoading = () => {
    // won't trigger requests if last commission date not found.
    if (
      date.includes('Since') &&
      props?.deviceData?.[0]?.lastCommissionDate === GET_LAST_COMMISSION_DATE_STATUS.NOT_FOUND
    ) {
      return false;
    }
    return requestSuccessCount === 0 && !requestError;
  };

  const commissionedDateNotFound = () => props.deviceData[0].lastCommissionDate === -1;

  const getAlarmsData = (index) => {
    const deviceId = props.deviceData[index]?.deviceId;
    const fieldName = viewBy === 'Alarms type' ? 'name' : 'severity';
    // return an empty chart
    if (alarmAggregateData?.[index]?.length === 0) {
      return {
        datasets: [
          {
            backgroundColor: ['#E8E8E8'],
            data: [-1],
            label: `${deviceId} alarm ${fieldName}`
          }
        ],
        labels: ['There is no alarm history for the date range.']
      };
    }

    const allAlarmTypes = [];
    alarmAggregateData.forEach((aggregateData) => {
      if (Array.isArray(aggregateData)) {
        aggregateData.forEach((data) => {
          if (!allAlarmTypes.includes(data[fieldName])) {
            allAlarmTypes.push(data[fieldName]);
          }
        });
      }
    });

    // sort to make charts color match
    allAlarmTypes.sort();

    let colors = [];
    let currLegendColors = { ...legendColors };
    allAlarmTypes.forEach((type, i) => {
      if (alarmAggregateData?.[index]) {
        const alarmIndex = alarmAggregateData[index].findIndex((typeObj) => typeObj[fieldName] === type);
        if (alarmIndex !== -1) {
          colors.push(DefaultPieChartColors[i]);
          const label = alarmAggregateData[index][colors.length - 1][fieldName];
          currLegendColors = { ...currLegendColors, [label]: DefaultPieChartColors[i] };
        }
      }
    });

    if (Object.keys(currLegendColors).length !== Object.keys(legendColors).length) {
      setLegendColors(currLegendColors);
    }

    return {
      datasets: [
        {
          backgroundColor: alarmAggregateData?.[index]?.map((data) => legendColors[data[fieldName]]),
          data: alarmAggregateData?.[index]?.map((data) => data.count),
          label: `${deviceId} alarm ${fieldName}`
        }
      ],
      labels: alarmAggregateData?.[index]?.map((data) => data[fieldName])
    };
  };

  const getDeviceIcon = (index) => {
    const deviceType = props.deviceData[index].deviceType;
    if (deviceType === 'donor') {
      return IconLoader.icon_donor;
    }

    if (deviceType === 'service') {
      return IconLoader.icon_service;
    }

    return IconLoader.icon_echo;
  };

  const getAlarmsContent = () => {
    if (isLoading()) {
      return <div className={styles.alarmsHistoryLoadingContainer}>{LOADING_CIRCLE}</div>;
    }
    if (commissionedDateNotFound() && date.includes('Since')) {
      return (
        <div className={styles.alarmHistoryWarningOverlay}>
          <WarningBox warningMessages={LAST_COMMISSION_DATE_NOT_FOUND_WARNING_MESSAGE} />
        </div>
      );
    }

    if (requestError) {
      return (
        <div className={styles.alarmHistoryContentContainer}>
          <div className="chartsErrorContainer">
            <div className={styles.alarmHistoryChartErrorBox}>
              <div className="chartErrorBoxTitle">Error</div>
              {
                <div className="chartErrorBoxMessage">
                  {ERROR_TERMINAL_MESSAGE_1}
                  <br />
                  <br />
                  <div>
                    {ERROR_TERMINAL_MESSAGE_2}
                    <a className="contactLink" href="mailto:support@pivotalcommware.com">
                      contact us
                    </a>
                    <span>.</span>
                  </div>
                </div>
              }
              <div className="chartErrorBoxCloseSection">
                <div className="chartErrorBoxCloseButton"></div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (alarmAggregateData.every((data) => !data || data === [])) {
      return <div> no data available </div>;
    }

    const fieldName = viewBy === 'Alarms type' ? 'name' : 'severity';
    const allAlarmTypes = [];
    if (Array.isArray(alarmAggregateData)) {
      alarmAggregateData.forEach((aggregateData) => {
        if (Array.isArray(aggregateData)) {
          aggregateData.forEach((data) => {
            if (!allAlarmTypes.includes(data[fieldName])) {
              allAlarmTypes.push(data[fieldName]);
            }
          });
        }
      });
    }

    // sort to make charts color match
    allAlarmTypes.sort();
    const legendItems = allAlarmTypes.map((alarmType, i) => ({ text: alarmType, fillStyle: legendColors[alarmType] }));

    return (
      <div className={styles.alarmHistoryContentContainer}>
        {props.deviceData.map((num, i) => (
          <div className={styles.singleDoughnutContainer} key={i}>
            <AlarmsDoughnut data={getAlarmsData(i)} />
            <div className={styles.alarmHistoryAzContainer}>
              <SvgAF img={getDeviceIcon(i)} />
              {/* “Az” (i.e. - Absolute Azimuth) = Mechanical Azimuth + Electrical Azimuth In the absence of user input of
              Azimuth (Mechanical), the “Az” should be reported as Azimuth (Electrical) with an asterisk (“*”) next to
              the “Az*”. */}
              <div className={styles.alarmHistoryAzContainer_text} data-tip="Az* means Azimuth (Electrical) only">
                {'Az* ' + props.deviceData[i].deviceAz}
              </div>
            </div>
          </div>
        ))}
        <div className={styles.alarmHistoryLegendContainer}>
          <ChartLegend items={legendItems} forAlarmHistory={true} />
        </div>
      </div>
    );
  };

  return (
    <div className={styles.alarmHistoryContainer}>
      <div className={styles.alarmHistoryTopContainer}>
        <div className={styles.alarmHistoryTitle}>Alarms</div>
        <div className={styles.alarmHistoryViewBy}>
          <div className={styles.alarmHistoryViewByText}>View by:</div>
          <div
            className={classNames({
              [styles.alarmHistoryViewBySelection]: !isLoading(),
              [styles.alarmHistoryViewBySelection_Disbaled]: isLoading() || props.deviceData === []
            })}
          >
            <Dropdown
              selectItem={(selection) => setViewBy(selection)}
              title={viewBy}
              data={['Alarms type', 'Alarms severity']}
              colorMode="clear"
              alarmsHistory={true}
            />
          </div>
        </div>
        <div
          className={classNames({
            [styles.alarmHistoryDateContainer]: !isLoading(),
            [styles.alarmHistoryDateContainer_disabled]: isLoading() || props.deviceData === []
          })}
        >
          <Dropdown
            selectItem={(selection) => dateDropdownOnClick(selection)}
            title={getDateDropdownTitle()}
            data={[sinceDateMessage, 'Select a date']}
            colorMode="clear"
            alarmsHistoryDate={true}
          />
          {showCalendar && (
            <div className={styles.alarmsHistoryCalendarContainer}>
              <SingleCalendar
                alarmHistory={true}
                isDateValid={(isValid) => {
                  setIsCalendarDateValid(isValid);
                }}
                sendRawDate={(rawDate) => {
                  if (rawDate) {
                    setRawDate(rawDate);
                  }
                }}
              />
              <div className={styles.AlarmHistoryCalendarButtonRow}>
                <button
                  className={styles.AlarmHistoryCalendarButtonRow_Cancel}
                  onClick={() => {
                    setShowCalendar(false);
                    setRawDate(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className={classNames({
                    [styles.AlarmHistoryCalendarButtonRow_Done]: isCalendarDateValid,
                    [styles.AlarmHistoryCalendarButtonRow_Done_Disabled]: !isCalendarDateValid
                  })}
                  disabled={!isCalendarDateValid}
                  onClick={() => {
                    handleDateSubmit();
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {getAlarmsContent()}
    </div>
  );
};

export default AlarmHistoryPanel;
